import { App, Button, DatePicker, Dropdown, Form, Input, Modal, Select, Space, Typography } from 'antd';
import type { MenuProps } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/lib/useAuth';
import { asUpdate, useMyStatus, useUpdateMyStatus, type Mode, type MyStatus, type StatusUpdate } from './api';
import { usePresence, usePresenceSync } from './hooks';
import { PresenceBadge } from './PresenceBadge';
import { expiryTime, type Expiry } from './expiry';
import type { CustomStatus } from './store';

/** The furthest ahead do-not-disturb or a status may end (server: 31 days). */
const MAX_DAYS = 31;

const EMOJIS = ['📅', '🏠', '🚗', '🎧', '🍽️', '✈️', '🏢', '💻', '🌴', '🎉'];

/** Presets offered in the status dialog. None hint at health or private life. */
const PRESETS: Array<{ emoji: string; key: string; expiry: Expiry }> = [
  { emoji: '📅', key: 'meeting', expiry: '1h' },
  { emoji: '🚗', key: 'commuting', expiry: '30m' },
  { emoji: '🏠', key: 'remote', expiry: 'today' },
  { emoji: '🎧', key: 'focusing', expiry: '1h' },
];

/**
 * Presence in the header (FR-PRES-04): your own badge, and the menu to set
 * away, appear offline, do not disturb and a custom status. Renders nothing,
 * and starts nothing, unless the caller has checked Collaboration is on.
 */
export function StatusMenu() {
  usePresenceSync();
  const { identity } = useAuth();
  const { data } = useMyStatus();
  // Your own badge is a watched person like any other.
  const me = usePresence(identity?.user_id);
  if (!data) return null;
  return <StatusMenuFor status={data} state={me?.state ?? data.presence.state} />;
}

function StatusMenuFor({ status, state }: { status: MyStatus; state: MyStatus['presence']['state'] }) {
  const { t } = useTranslation('collaboration');
  const { message } = App.useApp();
  const update = useUpdateMyStatus();
  const [dialog, setDialog] = useState<'status' | 'dnd' | null>(null);

  const change = (patch: Partial<StatusUpdate>) =>
    update.mutate({ ...asUpdate(status), ...patch }, { onError: () => void message.error(t('presence.menu.failed')) });

  const dndOn = Boolean(status.dnd_until && Date.parse(status.dnd_until) > Date.now());
  const modeItem = (mode: Mode, label: string) => ({
    key: `mode-${mode}`,
    icon: status.mode === mode ? <CheckOutlined /> : <span style={{ display: 'inline-block', width: 14 }} />,
    label,
    onClick: () => change({ mode }),
  });

  const items: MenuProps['items'] = [
    {
      key: 'current',
      disabled: true,
      label: (
        <Space direction="vertical" size={0}>
          <PresenceBadge state={state} showLabel />
          {status.status && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {status.status.emoji} {status.status.text}
            </Typography.Text>
          )}
          {dndOn && (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('presence.menu.dndUntil', { time: dayjs(status.dnd_until).format('D MMM, h:mm A') })}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    { type: 'divider' },
    { key: 'status', label: status.status ? t('presence.menu.editStatus') : t('presence.menu.setStatus'), onClick: () => setDialog('status') },
    ...(status.status ? [{ key: 'clear', label: t('presence.menu.clearStatus'), onClick: () => change({ status: null }) }] : []),
    { type: 'divider' },
    modeItem('auto', t('presence.menu.auto')),
    modeItem('away', t('presence.menu.away')),
    modeItem('invisible', t('presence.menu.invisible')),
    { type: 'divider' },
    dndOn
      ? { key: 'dnd-off', label: t('presence.menu.dndOff'), onClick: () => change({ dnd_until: null }) }
      : {
          key: 'dnd',
          label: t('presence.menu.dnd'),
          children: [
            { key: 'dnd-30', label: t('presence.menu.for30m'), onClick: () => change({ dnd_until: dayjs().add(30, 'minute').toISOString() }) },
            { key: 'dnd-60', label: t('presence.menu.for1h'), onClick: () => change({ dnd_until: dayjs().add(1, 'hour').toISOString() }) },
            {
              key: 'dnd-tomorrow',
              label: t('presence.menu.untilTomorrow'),
              onClick: () => change({ dnd_until: dayjs().add(1, 'day').hour(9).minute(0).second(0).toISOString() }),
            },
            { key: 'dnd-custom', label: t('presence.menu.custom'), onClick: () => setDialog('dnd') },
          ],
        },
  ];

  const stateLabel = t(`presence.${state === 'on_leave' ? 'onLeave' : state}`);
  return (
    <>
      <Dropdown menu={{ items }} trigger={['click']}>
        <Button
          type="text"
          aria-label={t('presence.menu.trigger', { status: stateLabel })}
          style={{ color: '#e2e8f0', display: 'inline-flex', alignItems: 'center', gap: 6 }}
        >
          <PresenceBadge state={state} theme="dark" />
          {status.status?.emoji && <span aria-hidden="true">{status.status.emoji}</span>}
        </Button>
      </Dropdown>
      <StatusDialog
        open={dialog === 'status'}
        initial={status.status}
        pending={update.isPending}
        onCancel={() => setDialog(null)}
        onSave={(s) => change({ status: s })}
        onDone={() => setDialog(null)}
      />
      <DndDialog
        open={dialog === 'dnd'}
        onCancel={() => setDialog(null)}
        onSave={(until) => {
          change({ dnd_until: until });
          setDialog(null);
        }}
      />
    </>
  );
}

export function StatusDialog({
  open,
  initial,
  pending,
  onCancel,
  onSave,
  onDone,
}: {
  open: boolean;
  initial: CustomStatus | null;
  pending: boolean;
  onCancel: () => void;
  onSave: (s: CustomStatus) => void;
  onDone: () => void;
}) {
  const { t } = useTranslation('collaboration');
  const [form] = Form.useForm<{ emoji?: string; text?: string; expiry: Expiry }>();

  const submit = (v: { emoji?: string; text?: string; expiry: Expiry }) => {
    const out: CustomStatus = {};
    if (v.emoji) out.emoji = v.emoji;
    if (v.text?.trim()) out.text = v.text.trim();
    const until = expiryTime(v.expiry);
    if (until) out.expires_at = until;
    if (!out.emoji && !out.text) return;
    onSave(out);
    onDone();
  };

  return (
    <Modal
      open={open}
      title={t('presence.status.title')}
      okText={t('presence.status.save')}
      onOk={() => form.submit()}
      okButtonProps={{ loading: pending }}
      onCancel={onCancel}
      destroyOnClose
    >
      <Form
        form={form}
        layout="vertical"
        preserve={false}
        initialValues={{ emoji: initial?.emoji, text: initial?.text, expiry: 'never' }}
        onFinish={submit}
      >
        <Form.Item label={t('presence.status.presets')}>
          <Space wrap>
            {PRESETS.map((p) => (
              <Button
                key={p.key}
                onClick={() => form.setFieldsValue({ emoji: p.emoji, text: t(`presence.status.preset.${p.key}`), expiry: p.expiry })}
              >
                {p.emoji} {t(`presence.status.preset.${p.key}`)}
              </Button>
            ))}
          </Space>
        </Form.Item>
        <Form.Item name="emoji" label={t('presence.status.emoji')}>
          <Select
            allowClear
            aria-label={t('presence.status.emoji')}
            options={EMOJIS.map((e) => ({ value: e, label: e }))}
            style={{ width: 96 }}
          />
        </Form.Item>
        <Form.Item name="text" label={t('presence.status.text')}>
          <Input maxLength={100} showCount placeholder={t('presence.status.placeholder')} />
        </Form.Item>
        <Form.Item name="expiry" label={t('presence.status.clearAfter')}>
          <Select
            options={(['never', '30m', '1h', 'today', 'week'] as const).map((e) => ({
              value: e,
              label: t(`presence.status.expiry.${e}`),
            }))}
          />
        </Form.Item>
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t('presence.status.visibility')}
        </Typography.Text>
      </Form>
    </Modal>
  );
}

function DndDialog({ open, onCancel, onSave }: { open: boolean; onCancel: () => void; onSave: (until: string) => void }) {
  const { t } = useTranslation('collaboration');
  const [until, setUntil] = useState<Dayjs | null>(null);
  return (
    <Modal
      open={open}
      title={t('presence.menu.dnd')}
      okButtonProps={{ disabled: !until }}
      onOk={() => until && onSave(until.toISOString())}
      onCancel={onCancel}
      destroyOnClose
    >
      <DatePicker
        showTime={{ format: 'h:mm A' }}
        format="D MMM YYYY, h:mm A"
        aria-label={t('presence.menu.until')}
        style={{ width: '100%' }}
        value={until}
        onChange={setUntil}
        disabledDate={(d) => d.isBefore(dayjs(), 'day') || d.isAfter(dayjs().add(MAX_DAYS, 'day'))}
      />
    </Modal>
  );
}
